/**
 * Session RPC Middleware Tag
 *
 * Validates a coding session: reads `x-session-id` + the `Authorization: Bearer`
 * session token, looks the session up, compares the token hash, and provides
 * SessionContext (the session's workspace + version) to handlers.
 *
 * This is the only middleware the session-scoped (CLI-facing) RPC group uses —
 * the session is the credential, so no user JWT is involved.
 *
 * @module
 */
import { RpcMiddleware } from "effect/unstable/rpc"
import { Context, Schema as S } from "effect"

// ============================================================================
// SessionContext Service
// ============================================================================

export type SessionOrigin = "browser" | "cli_local" | "remote_agent"

export interface SessionContextShape {
  readonly sessionId: string
  readonly workspaceId: string
  readonly versionId: string
  /** Source the version was forked from — a version id, a `tpl_` template id, or null. */
  readonly sourceVersionId: string | null
  /** The user the session is attributed to — needed to author a new version on rebind. */
  readonly createdByUserId: string | null
  /** Where this session edits from — drives concurrent-edit detection. */
  readonly origin: SessionOrigin
  /** The version revision this session last pulled from. */
  readonly baseRevision: number
  /** The revision at which this session acknowledged an override, if any. */
  readonly acknowledgedRevision: number | null
  /** The version's current revision at request time. */
  readonly currentRevision: number
}

/**
 * SessionContext provided by the session middleware. Available in handlers that
 * have `.middleware(RpcSessionMiddleware)`.
 */
export class SessionContext extends Context.Service<
  SessionContext,
  SessionContextShape
>()("@repo/rpc/SessionContext") {}

// ============================================================================
// Error
// ============================================================================

export class SessionAuthError extends S.TaggedErrorClass<SessionAuthError>()(
  "SessionAuthError",
  { message: S.String },
) {}

// ============================================================================
// Middleware Tag
// ============================================================================

export class RpcSessionMiddleware extends RpcMiddleware.Service<
  RpcSessionMiddleware,
  { provides: SessionContext }
>()("@repo/rpc/RpcSessionMiddleware", {
  error: SessionAuthError,
  requiredForClient: false,
}) {}
